/**
 * UrlaubUebersichtPage — HR Overview: Salden, Verfall-Warnungen, Sperren, Regeln.
 *
 * 4 Tabs: Salden | Verfall-Warnungen | Sperren | Regeln
 * Excel Export on Salden tab.
 *
 * @page /urlaub/uebersicht
 * @roles administrator, ops_manager, teamleiter
 * @since v2.198.0
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Download, ChevronDown, Info, Trash2, Plus, Save, Mail, AlertTriangle, X, CheckCircle, AlertCircle, Settings, User, Search, Calendar, Clock, Users, RefreshCw } from 'lucide-react';
import useTabParam from '../hooks/useTabParam';
import ModulEinstellungen from '../components/ModulEinstellungen';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import SharedAbwesenheitenTabs from '../components/SharedAbwesenheitenTabs';

const api = (path, opts) => fetch(path, { credentials: 'include', ...opts }).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)));
const apiPost = (path, body) => api(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
const apiPut = (path, body) => api(path, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
const apiDelete = (path) => api(path, { method: 'DELETE' });
const fmtDate = (s) => { if (!s) return '—'; const d = new Date(s); return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`; };

// ─── Tab Bar ───
function TabBar({ tabs, active, onChange }) {
  return (
    <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)] inline-flex gap-1 mb-5">
      {tabs.map(tab => (
        <button key={tab.key} onClick={() => onChange(tab.key)}
          className={`px-4 py-2 rounded-[10px] text-sm font-medium transition-all ${
            active === tab.key
              ? 'bg-white shadow-sm text-[var(--text-primary)] font-semibold dark:bg-[var(--bg-card)]'
              : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
          }`}>
          {tab.label}{tab.count != null ? ` (${tab.count})` : ''}
        </button>
      ))}
    </div>
  );
}

// ─── Kpi Card ───
function Kpi({ label, value, sub, icon: Icon, iconColor }) {
  return (
    <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-5 bg-[var(--bg-card,#fff)]">
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon size={16} className={iconColor || 'text-[#338edc]'} />}
        <p className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{label}</p>
      </div>
      <p className="text-2xl font-bold text-[var(--text-primary)]">{value}</p>
      {sub && <p className="text-sm text-[var(--text-muted)] mt-1">{sub}</p>}
    </div>
  );
}

// ═══ SALDEN TAB ═══
function SaldenTab({ t, konten, stats, onExport, onRowClick, onRecalcAll, recalcLoading }) {
  const [sortCol, setSortCol] = useState('ma_name');
  const [sortDir, setSortDir] = useState('asc');
  const [search, setSearch] = useState('');

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return konten;
    const q = search.trim().toLowerCase();
    return konten.filter(k => (k.ma_name || '').toLowerCase().includes(q) || (k.kostenstelle || '').toLowerCase().includes(q));
  }, [konten, search]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let va = a[sortCol], vb = b[sortCol];
      if (typeof va === 'string' && !isNaN(parseFloat(va))) { va = parseFloat(va); vb = parseFloat(vb); }
      if (typeof va === 'string') { va = va.toLowerCase(); vb = (vb || '').toLowerCase(); }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filtered, sortCol, sortDir]);

  const getStatus = (row) => {
    // v2.348.0: Zwei-Saldo-Logik
    const proj = parseFloat(row.projiziert_jahresende ?? row.verfuegbar_tage);
    const aktuell = parseFloat(row.aktuell_verfuegbar ?? row.verfuegbar_tage);
    const vvr = parseFloat(row.vorvorjahr_rest || 0);
    if (proj < 0) return { key: 'ueberzogen', cls: 'bg-red-100 text-red-700' };
    if (aktuell < 3) return { key: 'knapp', cls: 'bg-amber-100 text-amber-700' };
    if (vvr > 0) return { key: 'verfall', cls: 'bg-amber-100 text-amber-700' };
    return { key: 'ok', cls: 'bg-green-100 text-green-700' };
  };

  const fmt = (n) => {
    const num = parseFloat(n);
    if (isNaN(num)) return '0';
    return num.toLocaleString('de-DE', { minimumFractionDigits: num % 1 === 0 ? 0 : 1, maximumFractionDigits: 2 });
  };

  const colorForValue = (v) => {
    const n = parseFloat(v);
    if (isNaN(n)) return 'text-[var(--text-primary)]';
    if (n < 0) return 'text-red-600';
    if (n < 3) return 'text-amber-600';
    return 'text-emerald-600';
  };

  const SortHeader = ({ col, children, align }) => (
    <th
      className={`${align === 'right' ? 'text-right' : 'text-left'} text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] px-4 py-3 cursor-pointer select-none hover:text-[var(--text-primary)]`}
      onClick={() => toggleSort(col)}
    >
      <span className="inline-flex items-center gap-1">{children} {sortCol === col && <ChevronDown size={12} className={sortDir === 'asc' ? 'rotate-180' : ''} />}</span>
    </th>
  );

  return (
    <>
      {/* KPI bar */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-5">
        <Kpi icon={Calendar} iconColor="text-[#338edc]" label={t('urlaub.uebersicht.avgRest')} value={`${stats.avg_resturlaub} ${t('urlaub.tage')}`} sub={`${stats.ma_count} ${t('nav.employees')}`} />
        <Kpi icon={AlertTriangle} iconColor="text-[#EF4444]" label={t('urlaub.uebersicht.verfallGefaehrdet')} value={`${stats.verfallsgefaehrdet_count} MA`} />
        <Kpi icon={Users} iconColor="text-[#8B5CF6]" label={t('urlaub.uebersicht.heuteAbwesend')} value={`${stats.heute_abwesend} MA`} />
        <Kpi icon={Clock} iconColor="text-[#22D3EE]" label={t('krankenstand.avgProMa')} value={stats.ks_avg_tage_pro_ma != null ? `${stats.ks_avg_tage_pro_ma} ${t('urlaub.tage')}` : '–'} />
        <Kpi icon={AlertCircle} iconColor="text-[#F59E0B]" label={t('krankenstand.warnung')} value={stats.ks_ma_ueber_schwelle != null ? `${stats.ks_ma_ueber_schwelle} MA` : '–'} sub={stats.ks_schwelle ? `> ${stats.ks_schwelle} ${t('urlaub.tage')}` : ''} />
      </div>

      {/* Search + Export */}
      <div className="flex items-center justify-between mb-3">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input
            type="text"
            placeholder={t('urlaub.antraege.searchPlaceholder', 'Mitarbeiter suchen...')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-8 pr-3 py-1.5 text-xs border border-[var(--border-default)] rounded-lg bg-[var(--bg-card)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] w-48"
          />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onRecalcAll} disabled={recalcLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-[10px] border border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset)] disabled:opacity-50">
            <RefreshCw size={14} className={recalcLoading ? 'animate-spin' : ''} />
            {recalcLoading ? t('urlaub.recalculating', 'Wird berechnet...') : t('urlaub.alleSaldenBerechnen', 'Alle Salden berechnen')}
          </button>
          <button onClick={onExport}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-[10px] border border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]">
            <Download size={14} /> {t('urlaub.uebersicht.exportExcel')}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--bg-inset,#F8F9FC)]">
                <SortHeader col="ma_name">{t('nav.employees')}</SortHeader>
                <SortHeader col="anspruch_tage" align="right">{t('urlaub.anspruch')}</SortHeader>
                <SortHeader col="vorjahr_rest" align="right">{t('urlaub.vorjahrRest')}</SortHeader>
                <SortHeader col="genommen" align="right">{t('urlaub.saldo.genommen', 'Genommen')}</SortHeader>
                <SortHeader col="geplant" align="right">{t('urlaub.saldo.geplant', 'Geplant')}</SortHeader>
                <SortHeader col="aktuell_verfuegbar" align="right">{t('urlaub.saldo.aktuellVerfuegbar', 'Aktuell verf.')}</SortHeader>
                <SortHeader col="projiziert_jahresende" align="right">{t('urlaub.saldo.projiziertJE', 'Projiziert JE')}</SortHeader>
                <th className="text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-default,#E5E7EB)]">
              {sorted.map(row => {
                const st = getStatus(row);
                const laufendCnt = parseInt(row.laufend_anzahl || 0);
                const geplantCnt = parseInt(row.geplant_anzahl || 0);
                return (
                  <tr key={row.id} className="hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors cursor-pointer" onClick={() => onRowClick?.(row.mitarbeiter_id)}>
                    <td className="px-4 py-3 font-medium text-[var(--text-primary)] whitespace-nowrap">
                      {row.ma_name}
                      {row.kostenstelle && <span className="ml-1.5 text-[10px] text-[var(--text-muted)]">{row.kostenstelle}</span>}
                    </td>
                    <td className="px-4 py-3 text-right text-[var(--text-primary)]">{fmt(row.anspruch_tage)}</td>
                    <td className="px-4 py-3 text-right text-[var(--text-primary)]">{fmt(row.vorjahr_rest)}</td>
                    <td className="px-4 py-3 text-right text-[var(--text-primary)]">
                      {fmt(row.genommen ?? row.verbraucht_tage)}
                      {laufendCnt > 0 && <span className="ml-1 text-[10px] text-blue-600 font-medium">({t('urlaub.saldo.laufend', 'laufend')})</span>}
                    </td>
                    <td className="px-4 py-3 text-right text-[var(--text-primary)]">
                      {fmt(row.geplant ?? row.geplant_tage)}
                      {geplantCnt > 0 && <span className="ml-1 text-[10px] text-[var(--text-muted)]">({geplantCnt})</span>}
                    </td>
                    <td className={`px-4 py-3 text-right font-semibold ${colorForValue(row.aktuell_verfuegbar)}`}>{fmt(row.aktuell_verfuegbar)}</td>
                    <td className={`px-4 py-3 text-right font-semibold ${colorForValue(row.projiziert_jahresende)}`}>{fmt(row.projiziert_jahresende)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${st.cls}`}>
                        {t(`urlaub.uebersicht.status${st.key.charAt(0).toUpperCase() + st.key.slice(1)}`)}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {sorted.length === 0 && (
                <tr><td colSpan={8} className="text-center py-8 text-sm text-[var(--text-muted)]">{t('common.noData') || 'Keine Daten'}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ═══ VERFALL TAB ═══
function VerfallTab({ t, konten, isAdmin }) {
  const [sending, setSending] = useState(null);
  const [sent, setSent] = useState(new Set());
  const [error, setError] = useState(null);

  const gefaehrdet = useMemo(() =>
    konten.filter(k => parseFloat(k.vorvorjahr_rest) > 0),
  [konten]);

  const jahr = new Date().getFullYear();

  const sendReminder = async (maId) => {
    setSending(maId);
    setError(null);
    try {
      await apiPost(`/api/urlaub/verfall-erinnerung/${maId}?jahr=${jahr}`);
      setSent(s => new Set(s).add(maId));
    } catch (err) {
      setError(err.error || err.message || 'Fehler');
    }
    setSending(null);
  };

  return (
    <>
      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-start gap-3 mb-5">
        <Info size={18} className="text-blue-500 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-800">
          <strong>{t('urlaub.uebersicht.verfallInfoTitle')}</strong><br />
          {t('urlaub.uebersicht.verfallInfoText')}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <AlertTriangle size={14} /> {error}
          <button onClick={() => setError(null)} className="ml-auto"><X size={14} /></button>
        </div>
      )}

      {gefaehrdet.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[var(--bg-inset,#F8F9FC)] flex items-center justify-center mb-4">
            <CheckCircle size={28} className="text-green-500" />
          </div>
          <h3 className="text-base font-semibold text-[var(--text-primary)] mb-1">{t('urlaub.uebersicht.keinVerfall')}</h3>
          <p className="text-sm text-[var(--text-tertiary)]">{t('urlaub.uebersicht.keinVerfallDesc')}</p>
        </div>
      ) : (
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--bg-inset,#F8F9FC)]">
                <th className="text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] px-4 py-3">{t('nav.employees')}</th>
                <th className="text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] px-4 py-3">{t('urlaub.uebersicht.restVorvorjahr')}</th>
                <th className="text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] px-4 py-3">{t('urlaub.uebersicht.verfaelltAm')}</th>
                {isAdmin && <th className="text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] px-4 py-3">{t('urlaub.uebersicht.aktion')}</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-default,#E5E7EB)]">
              {gefaehrdet.map(row => (
                <tr key={row.id} className="hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors">
                  <td className="px-4 py-3 font-medium text-[var(--text-primary)]">{row.ma_name}</td>
                  <td className="px-4 py-3 text-right font-semibold text-[var(--text-primary)]">
                    {parseFloat(row.vorvorjahr_rest)} {t('urlaub.tage')}
                  </td>
                  <td className="px-4 py-3 text-[var(--text-primary)]">31.12.{jahr}</td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-right">
                      {sent.has(row.mitarbeiter_id) ? (
                        <span className="text-xs text-green-700 font-medium flex items-center justify-end gap-1"><CheckCircle size={12} /> {t('urlaub.uebersicht.gesendet')}</span>
                      ) : (
                        <button onClick={() => sendReminder(row.mitarbeiter_id)} disabled={sending === row.mitarbeiter_id}
                          className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-[10px] border border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset)] disabled:opacity-50 ml-auto">
                          {sending === row.mitarbeiter_id ? '...' : <><Mail size={12} /> {t('urlaub.uebersicht.erinnern')}</>}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ═══ SPERREN TAB ═══
function SperrenTab({ t, sperren, onReload, isAdmin }) {
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ von_datum: '', bis_datum: '', grund: '', abteilung: '' });
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const handleCreate = async () => {
    if (!form.von_datum || !form.bis_datum || !form.grund) return;
    setSubmitting(true);
    try {
      await apiPost('/api/urlaub/sperren', {
        von_datum: form.von_datum,
        bis_datum: form.bis_datum,
        grund: form.grund,
        abteilung: form.abteilung || null,
      });
      setModal(false);
      setForm({ von_datum: '', bis_datum: '', grund: '', abteilung: '' });
      onReload();
    } catch {}
    setSubmitting(false);
  };

  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      await apiDelete(`/api/urlaub/sperren/${id}`);
      onReload();
    } catch {}
    setDeleting(null);
  };

  const aktive = sperren.filter(s => s.aktiv);

  return (
    <>
      {isAdmin && (
        <div className="flex justify-end mb-4">
          <button onClick={() => setModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold transition-colors">
            <Plus size={16} /> {t('urlaub.uebersicht.neueSperreTitle')}
          </button>
        </div>
      )}

      {aktive.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <p className="text-sm text-[var(--text-tertiary)]">{t('urlaub.uebersicht.keineSperren')}</p>
        </div>
      ) : (
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--bg-inset,#F8F9FC)]">
                <th className="text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] px-4 py-3">{t('urlaub.antraege.zeitraum')}</th>
                <th className="text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] px-4 py-3">{t('urlaub.uebersicht.sperreGrund')}</th>
                <th className="text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] px-4 py-3">{t('urlaub.uebersicht.sperreBereich')}</th>
                <th className="text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] px-4 py-3">{t('urlaub.antraege.erstelltAm')}</th>
                {isAdmin && <th className="text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] px-4 py-3"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-default,#E5E7EB)]">
              {aktive.map(s => (
                <tr key={s.id} className="hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors">
                  <td className="px-4 py-3 text-[var(--text-primary)] whitespace-nowrap">{fmtDate(s.von_datum)} – {fmtDate(s.bis_datum)}</td>
                  <td className="px-4 py-3 text-[var(--text-primary)]">{s.grund}</td>
                  <td className="px-4 py-3 text-[var(--text-secondary)]">{s.abteilung || s.standort_name || t('urlaub.uebersicht.alleBereich')}</td>
                  <td className="px-4 py-3 text-[var(--text-tertiary)] whitespace-nowrap">{fmtDate(s.created_at)}</td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => handleDelete(s.id)} disabled={deleting === s.id}
                        className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-50">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setModal(false)}>
          <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] w-full max-w-md mx-4"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-card,#E5E7EB)]">
              <h2 className="text-base font-semibold text-[var(--text-primary)]">{t('urlaub.uebersicht.neueSperreTitle')}</h2>
              <button onClick={() => setModal(false)} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('urlaub.antrag.von')}</label>
                  <input type="date" className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)]"
                    value={form.von_datum} onChange={e => setForm(f => ({ ...f, von_datum: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('urlaub.antrag.bis')}</label>
                  <input type="date" className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)]"
                    value={form.bis_datum} onChange={e => setForm(f => ({ ...f, bis_datum: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('urlaub.uebersicht.sperreGrund')}</label>
                <input type="text" className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)]"
                  value={form.grund} onChange={e => setForm(f => ({ ...f, grund: e.target.value }))} placeholder={t('urlaub.uebersicht.sperreGrundPlaceholder')} />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('urlaub.uebersicht.sperreBereich')}</label>
                <select className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)]"
                  value={form.abteilung} onChange={e => setForm(f => ({ ...f, abteilung: e.target.value }))}>
                  <option value="">{t('urlaub.uebersicht.alleBereich')}</option>
                  <option value="outbound">Outbound</option>
                  <option value="inbound_retouren">Inbound & Retouren</option>
                  <option value="verwaltung">Verwaltung</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)]">
              <button onClick={() => setModal(false)}
                className="px-4 py-2 rounded-[10px] border border-[var(--border-card,#E5E7EB)] text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover,#FAFAFE)]">
                {t('common.cancel') || 'Abbrechen'}
              </button>
              <button onClick={handleCreate} disabled={submitting || !form.von_datum || !form.bis_datum || !form.grund}
                className="px-4 py-2 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold disabled:opacity-50 transition-colors">
                {submitting ? '...' : t('common.save') || 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ═══ REGELN TAB ═══
function RegelnTab({ t, regeln, onReload, isAdmin }) {
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(null);

  const BEREICH_LABELS = {
    outbound: 'Outbound',
    inbound_retouren: 'Inbound & Retouren',
    verwaltung: 'Verwaltung',
    gesamt: 'Gesamt',
  };

  const handleSave = async (regel) => {
    const val = edits[regel.id];
    if (val == null || val === regel.max_gleichzeitig) return;
    setSaving(regel.id);
    try {
      await apiPut(`/api/urlaub/regeln/${regel.id}`, { max_gleichzeitig: parseInt(val) });
      onReload();
      setEdits(e => { const n = { ...e }; delete n[regel.id]; return n; });
    } catch {}
    setSaving(null);
  };

  return (
    <>
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-start gap-3 mb-5">
        <Info size={18} className="text-blue-500 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-blue-800">{t('urlaub.uebersicht.regelnInfo')}</p>
      </div>

      <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[var(--bg-inset,#F8F9FC)]">
              <th className="text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] px-4 py-3">{t('urlaub.bereich')}</th>
              <th className="text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] px-4 py-3">{t('urlaub.uebersicht.gleichzeitigMax')}</th>
              {isAdmin && <th className="text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] px-4 py-3"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--border-default,#E5E7EB)]">
            {regeln.filter(r => r.aktiv).map(regel => {
              const editVal = edits[regel.id];
              const isDirty = editVal != null && parseInt(editVal) !== regel.max_gleichzeitig;
              return (
                <tr key={regel.id} className="hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors">
                  <td className="px-4 py-3 font-medium text-[var(--text-primary)]">{BEREICH_LABELS[regel.bereich] || regel.bereich}</td>
                  <td className="px-4 py-3">
                    {isAdmin ? (
                      <input
                        type="number" min={0} max={50}
                        className="w-20 px-3 py-1.5 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)] text-[var(--text-primary)] text-center"
                        value={editVal != null ? editVal : regel.max_gleichzeitig}
                        onChange={e => setEdits(prev => ({ ...prev, [regel.id]: e.target.value }))}
                      />
                    ) : (
                      <span className="text-[var(--text-primary)] font-semibold">{regel.max_gleichzeitig}</span>
                    )}
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => handleSave(regel)} disabled={!isDirty || saving === regel.id}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white disabled:opacity-30 ml-auto transition-colors">
                        {saving === regel.id ? '...' : <><Save size={12} /> {t('common.save') || 'Speichern'}</>}
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ═══ BUCHUNG MODAL ═══
function BuchungModal({ maId, maName, onClose, onSaved, t }) {
  const [typ, setTyp] = useState('bonus');
  const [tage, setTage] = useState('');
  const [begruendung, setBegruendung] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!tage || !begruendung || begruendung.length < 5) return;
    setSaving(true);
    setError(null);
    try {
      await apiPost('/api/urlaub/konto-buchung', {
        mitarbeiter_id: maId,
        typ,
        tage: parseFloat(tage),
        begruendung,
      });
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.error || err.message || 'Fehler');
    }
    setSaving(false);
  };

  const TYP_LABELS = {
    bonus: { de: 'Bonus', en: 'Bonus' },
    korrektur: { de: 'Korrektur', en: 'Correction' },
    uebertrag: { de: 'Übertrag', en: 'Carry-over' },
    abzug: { de: 'Abzug', en: 'Deduction' },
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-card,#E5E7EB)]">
          <h3 className="text-base font-semibold text-[var(--text-primary)]">{t('urlaub.buchung.title', 'Tage zuweisen')}</h3>
          <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">{error}</div>}
          <div className="text-sm text-[var(--text-secondary)]">{maName}</div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('urlaub.buchung.typ', 'Typ')}</label>
            <select className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)]"
              value={typ} onChange={e => setTyp(e.target.value)}>
              {Object.entries(TYP_LABELS).map(([k, v]) => <option key={k} value={k}>{v.de}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('urlaub.tage')}</label>
            <input type="number" step="0.5" min="-50" max="50"
              className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)]"
              value={tage} onChange={e => setTage(e.target.value)}
              placeholder={typ === 'abzug' ? '-2' : '1'} />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('urlaub.buchung.begruendung', 'Begründung')} *</label>
            <textarea rows={2} className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)] resize-none"
              value={begruendung} onChange={e => setBegruendung(e.target.value)}
              placeholder={t('urlaub.buchung.begruendungPlaceholder', 'Min. 5 Zeichen')} />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-[var(--border-default,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)]">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-[10px] border border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover,#FAFAFE)]">
            {t('common.cancel', 'Abbrechen')}
          </button>
          <button onClick={handleSubmit} disabled={saving || !tage || !begruendung || begruendung.length < 5}
            className="px-4 py-2 text-sm rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white font-semibold disabled:opacity-50 transition-colors">
            {saving ? '...' : t('urlaub.buchung.buchen', 'Buchen')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══ SALDO DRAWER ═══
function SaldoDrawer({ maId, onClose, onAntragErstellen, t, isAdmin }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buchungen, setBuchungen] = useState([]);
  const [buchungModal, setBuchungModal] = useState(false);

  const loadData = useCallback(() => {
    if (!maId) return;
    setLoading(true);
    Promise.all([
      api(`/api/urlaub/saldo-detail/${maId}`),
      api(`/api/urlaub/konto-buchungen/${maId}`),
    ]).then(([r, b]) => {
      setData(r.data);
      setBuchungen(b.data || []);
    }).catch(() => {})
      .finally(() => setLoading(false));
  }, [maId]);

  useEffect(() => { loadData(); }, [loadData]);

  if (!maId) return null;

  const STATUS_CLS = {
    beantragt: 'bg-yellow-50 text-yellow-800',
    genehmigt: 'bg-green-50 text-green-800',
    abgelehnt: 'bg-red-50 text-red-800',
    angetreten: 'bg-blue-50 text-blue-800',
    abgeschlossen: 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary,#6B7280)]',
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-2xl bg-[var(--bg-card,#fff)] border-l border-[var(--border-default,#E5E7EB)] overflow-y-auto h-full"
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="px-6 py-5 border-b border-[var(--border-default,#E5E7EB)] flex items-center justify-between sticky top-0 bg-[var(--bg-card,#fff)] z-10">
          <div>
            {data?.ma ? (
              <>
                <h2 className="text-lg font-semibold text-[var(--text-primary)]">{data.ma.vorname} {data.ma.nachname}</h2>
                <p className="text-sm text-[var(--text-secondary)]">
                  {data.ma.kostenstelle || data.ma.department_name || ''}
                  {data.ma.eintrittsdatum && ` · ${t('urlaub.arbeitsjahr')}: ${fmtDate(data.konto?.urlaubsjahr_von)} – ${fmtDate(data.konto?.urlaubsjahr_bis)}`}
                </p>
              </>
            ) : (
              <h2 className="text-lg font-semibold text-[var(--text-primary)]">{t('urlaub.saldoDetail')}</h2>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button
                onClick={() => setBuchungModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-[10px] border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover,#FAFAFE)] transition-colors"
              >
                <Plus size={14} /> {t('urlaub.buchung.title', 'Tage zuweisen')}
              </button>
            )}
            {onAntragErstellen && (
              <button
                onClick={() => onAntragErstellen(maId)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white transition-colors"
              >
                <Plus size={14} /> {t('urlaub.antragEinreichen')}
              </button>
            )}
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-[var(--bg-inset)] text-[var(--text-tertiary)]">
              <X size={20} />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-60 text-[var(--text-muted)]">
            <div className="w-5 h-5 border-2 border-[var(--primary)] border-t-transparent rounded-full animate-spin mr-2" />
          </div>
        ) : !data ? (
          <div className="text-center py-16 text-sm text-[var(--text-muted)]">{t('common.noData')}</div>
        ) : (
          <div className="px-6 py-5 space-y-6">
            {/* v2.348.0 Zwei-Saldo-Blöcke — #1698: JE-Block entfernt, Stand heute + Prognose Ende Urlaubsjahr */}
            <div className={`grid grid-cols-1 ${data.prognose_urlaubsjahr != null ? 'md:grid-cols-2' : ''} gap-4`}>
              {/* Block 1: Stand heute */}
              <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Clock size={16} className="text-[#338edc]" />
                  <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t('urlaub.saldo.standHeute', 'Stand heute')}</h3>
                </div>
                <p className={`text-3xl font-bold ${parseFloat(data.aktuell_verfuegbar ?? 0) < 0 ? 'text-red-600' : parseFloat(data.aktuell_verfuegbar ?? 0) < 3 ? 'text-amber-600' : 'text-emerald-600'}`}>
                  {parseFloat(data.aktuell_verfuegbar ?? 0).toFixed(2)} <span className="text-sm font-normal text-[var(--text-secondary)]">{t('urlaub.tage')}</span>
                </p>
                <p className="text-xs text-[var(--text-muted)] mt-1">{t('urlaub.saldo.aktuellVerfuegbar', 'Aktuell verfügbar')}</p>
                <div className="space-y-1.5 text-xs mt-4 pt-3 border-t border-[var(--border-default)]">
                  {data.konto?.startsaldo != null ? (
                    <div className="flex justify-between">
                      <span className="text-[var(--text-secondary)]">{t('urlaub.saldo.startsaldo', 'Startsaldo')}</span>
                      <span className="font-medium text-[var(--text-primary)]">{parseFloat(data.konto.startsaldo).toFixed(2)}</span>
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-between">
                        <span className="text-[var(--text-secondary)]">{t('urlaub.vorjahrRest')}</span>
                        <span className="font-medium text-[var(--text-primary)]">{parseFloat(data.konto?.vorjahr_rest || 0).toFixed(2)}</span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between">
                    <span className="text-[var(--text-secondary)]">+ {t('urlaub.saldo.aufbauBisHeute', 'Aufbau bis heute')}</span>
                    <span className="font-medium text-[var(--text-primary)]">{parseFloat(data.konto?.aufgebaut_aktuell || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--text-secondary)]">− {t('urlaub.saldo.genommen', 'Genommen')}</span>
                    <span className="font-medium text-[var(--text-primary)]">{parseFloat(data.genommen ?? data.verbraucht ?? 0).toFixed(2)}</span>
                  </div>
                  {parseFloat(data.konto?.laufend || 0) > 0 && (
                    <div className="flex justify-between text-blue-600">
                      <span>{t('urlaub.saldo.laufendAktiv', 'Laufend aktiv')} ({data.konto?.laufend_anzahl || 0})</span>
                      <span className="font-medium">{parseFloat(data.konto.laufend).toFixed(2)} {t('urlaub.tage')}</span>
                    </div>
                  )}
                  {parseFloat(data.buchungen || 0) !== 0 && (
                    <div className="flex justify-between">
                      <span className="text-[var(--text-secondary)]">{parseFloat(data.buchungen) >= 0 ? '+' : '−'} {t('urlaub.buchung.title', 'Buchungen')}</span>
                      <span className="font-medium text-[var(--text-primary)]">{Math.abs(parseFloat(data.buchungen)).toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Block 2: Prognose Ende Urlaubsjahr (#1572, #1698: JE-Block entfernt) — nur wenn eintrittsbasiertes Urlaubsjahr berechenbar */}
              {data.prognose_urlaubsjahr != null && (
                <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar size={16} className="text-[#338edc]" />
                    <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t('urlaub.saldo.prognoseUJ', 'Prognose Ende Urlaubsjahr')}</h3>
                  </div>
                  <p className={`text-3xl font-bold ${parseFloat(data.prognose_urlaubsjahr) < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    {parseFloat(data.prognose_urlaubsjahr).toFixed(2)} <span className="text-sm font-normal text-[var(--text-secondary)]">{t('urlaub.tage')}</span>
                  </p>
                  <p className="text-xs text-[var(--text-muted)] mt-1">{t('urlaub.saldo.bisDatum', { datum: fmtDate(data.urlaubsjahr_bis), defaultValue: 'bis {{datum}}' })}</p>
                </div>
              )}
            </div>

            {/* Bar visualization */}
            {(() => {
              const anspruch = parseFloat(data.konto?.anspruch_tage || 0);
              const vorjahr = parseFloat(data.konto?.vorjahr_rest || 0);
              const startsaldo = data.konto?.startsaldo != null ? parseFloat(data.konto.startsaldo) : null;
              const basis = startsaldo != null ? startsaldo + anspruch : vorjahr + anspruch;
              if (basis <= 0) return null;
              const genommen = parseFloat(data.genommen ?? data.verbraucht ?? 0);
              const laufend = parseFloat(data.konto?.laufend || 0);
              const geplant = parseFloat(data.geplant_future ?? data.geplant ?? 0);
              const rest = Math.max(0, basis - genommen - laufend - geplant);
              const pct = (v) => Math.max(0, Math.min(100, (v / basis) * 100));
              return (
                <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-3">{t('urlaub.saldo.verteilung', 'Jahresverteilung')} ({basis.toFixed(1)} {t('urlaub.tage')})</h3>
                  <div className="w-full h-7 rounded-lg overflow-hidden flex border border-[var(--border-default)]">
                    {genommen > 0 && <div className="bg-red-400 flex items-center justify-center text-[10px] text-white font-medium" style={{ width: `${pct(genommen)}%` }} title={`${t('urlaub.saldo.genommen', 'Genommen')}: ${genommen.toFixed(1)}`}>{pct(genommen) > 8 ? genommen.toFixed(1) : ''}</div>}
                    {laufend > 0 && <div className="bg-blue-400 flex items-center justify-center text-[10px] text-white font-medium" style={{ width: `${pct(laufend)}%` }} title={`${t('urlaub.saldo.laufend', 'Laufend')}: ${laufend.toFixed(1)}`}>{pct(laufend) > 8 ? laufend.toFixed(1) : ''}</div>}
                    {geplant > 0 && <div className="bg-amber-400 flex items-center justify-center text-[10px] text-white font-medium" style={{ width: `${pct(geplant)}%` }} title={`${t('urlaub.saldo.geplant', 'Geplant')}: ${geplant.toFixed(1)}`}>{pct(geplant) > 8 ? geplant.toFixed(1) : ''}</div>}
                    {rest > 0 && <div className="bg-emerald-400 flex items-center justify-center text-[10px] text-white font-medium" style={{ width: `${pct(rest)}%` }} title={`${t('urlaub.saldo.puffer', 'Puffer')}: ${rest.toFixed(1)}`}>{pct(rest) > 8 ? rest.toFixed(1) : ''}</div>}
                  </div>
                  <div className="flex flex-wrap gap-3 mt-3 text-[11px]">
                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-red-400"></span><span className="text-[var(--text-secondary)]">{t('urlaub.saldo.genommen', 'Genommen')}</span></span>
                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-blue-400"></span><span className="text-[var(--text-secondary)]">{t('urlaub.saldo.laufend', 'Laufend')}</span></span>
                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-400"></span><span className="text-[var(--text-secondary)]">{t('urlaub.saldo.geplant', 'Geplant')}</span></span>
                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-400"></span><span className="text-[var(--text-secondary)]">{t('urlaub.saldo.puffer', 'Puffer')}</span></span>
                  </div>
                </div>
              );
            })()}

            {/* Aliquotierung hint */}
            {data.konto?.in_wartefrist && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-800">
                {t('urlaub.wartefrist')} — {t('urlaub.aliquotiertHinweis', { tage: parseFloat(data.konto.aufgebaut_aktuell || 0).toFixed(2), von: parseFloat(data.konto.anspruch_tage || 0) })}
              </div>
            )}

            {/* Aufbau chart */}
            {data.aufbauChart?.length > 0 && (
              <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-5">
                <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-3">{t('urlaub.aufbauVerlauf')}</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={data.aufbauChart}>
                    <XAxis dataKey="monat" tick={{ fontSize: 11, fill: 'var(--text-tertiary)' }} />
                    <YAxis tick={{ fontSize: 11, fill: 'var(--text-tertiary)' }} />
                    <Tooltip />
                    <Area type="monotone" dataKey="aufgebaut" fill="#338edc" fillOpacity={0.1} stroke="#338edc" strokeWidth={2} name={t('urlaub.aufgebaut')} />
                    <Area type="monotone" dataKey="verfuegbar" fill="#22D3EE" fillOpacity={0.1} stroke="#22D3EE" strokeWidth={2} name={t('urlaub.verfuegbar')} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Antrags-Historie */}
            <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden">
              <div className="px-4 py-3 bg-[var(--bg-inset,#F8F9FC)]">
                <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t('urlaub.antragsHistorie')}</h3>
              </div>
              {data.antraege?.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[var(--bg-inset,#F8F9FC)]">
                      <th className="px-4 py-2 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('urlaub.antraege.zeitraum', 'Zeitraum')}</th>
                      <th className="px-4 py-2 text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('urlaub.tage')}</th>
                      <th className="px-4 py-2 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('urlaub.antrag.typ', 'Typ')}</th>
                      <th className="px-4 py-2 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--border-default,#E5E7EB)]">
                    {data.antraege.map(a => (
                      <tr key={a.id} className="hover:bg-[var(--bg-inset,#F8F9FC)]">
                        <td className="px-4 py-2.5 text-[var(--text-primary)] whitespace-nowrap">{fmtDate(a.von_datum)} – {fmtDate(a.bis_datum)}</td>
                        <td className="px-4 py-2.5 text-right font-semibold text-[var(--text-primary)]">{parseFloat(a.tage)}</td>
                        <td className="px-4 py-2.5 text-[var(--text-secondary)]">{t(`urlaub.typ.${a.typ}`) || a.typ}</td>
                        <td className="px-4 py-2.5">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_CLS[a.status] || 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary,#6B7280)]'}`}>
                            {t(`urlaub.status.${a.status}`) || a.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-8 text-sm text-[var(--text-muted)]">{t('common.noData')}</div>
              )}
            </div>

            {/* Buchungen-Historie */}
            {buchungen.length > 0 && (
              <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden">
                <div className="px-4 py-3 bg-[var(--bg-inset,#F8F9FC)]">
                  <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t('urlaub.buchung.historie', 'Buchungen')}</h3>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[var(--bg-inset,#F8F9FC)]">
                      <th className="px-4 py-2 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('common.date', 'Datum')}</th>
                      <th className="px-4 py-2 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('urlaub.buchung.typ', 'Typ')}</th>
                      <th className="px-4 py-2 text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('urlaub.tage')}</th>
                      <th className="px-4 py-2 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('urlaub.buchung.begruendung', 'Begründung')}</th>
                      <th className="px-4 py-2 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('urlaub.buchung.von', 'Von')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--border-default,#E5E7EB)]">
                    {buchungen.map(b => (
                      <tr key={b.id} className="hover:bg-[var(--bg-inset,#F8F9FC)]">
                        <td className="px-4 py-2.5 text-[var(--text-primary)] whitespace-nowrap">{fmtDate(b.gebucht_am)}</td>
                        <td className="px-4 py-2.5 text-[var(--text-secondary)] capitalize">{t(`urlaub.buchung.typ.${b.typ}`, b.typ)}</td>
                        <td className={`px-4 py-2.5 text-right font-semibold ${parseFloat(b.tage) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {parseFloat(b.tage) >= 0 ? '+' : ''}{parseFloat(b.tage).toFixed(1)}
                        </td>
                        <td className="px-4 py-2.5 text-[var(--text-secondary)] max-w-[200px] truncate">{b.begruendung}</td>
                        <td className="px-4 py-2.5 text-[var(--text-tertiary)] whitespace-nowrap">{b.gebucht_von_name || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Buchung Modal */}
        {buchungModal && (
          <BuchungModal
            maId={maId}
            maName={data?.ma ? `${data.ma.vorname} ${data.ma.nachname}` : ''}
            onClose={() => setBuchungModal(false)}
            onSaved={loadData}
            t={t}
          />
        )}
      </div>
    </div>
  );
}

// ═══ ANTRAG MODAL (from SaldoDrawer) ═══
function NeuAntragDrawerModal({ maId, onClose, onSaved, t }) {
  const [abwesenheitsarten, setAbwesenheitsarten] = useState([]);
  const [typ, setTyp] = useState('urlaub');
  const [von, setVon] = useState('');
  const [bis, setBis] = useState('');
  const [notiz, setNotiz] = useState('');
  const [stundenweise, setStundenweise] = useState(false);
  const [vonZeit, setVonZeit] = useState('');
  const [bisZeit, setBisZeit] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [konto, setKonto] = useState(null);

  useEffect(() => {
    if (maId) api(`/api/urlaub/konto/${maId}`).then(r => setKonto(r.aktuell || null)).catch(() => {});
    api('/api/abwesenheitsarten').then(r => setAbwesenheitsarten((r.data || []).filter(a => a.aktiv))).catch(() => {});
  }, [maId]);

  const selectedArt = abwesenheitsarten.find(a => a.key === typ);
  const quelle = selectedArt?.quelle || 'urlaub_antraege';
  const verfuegbar = konto ? parseFloat(konto.saldo || 0) : null;

  const handleSubmit = async () => {
    if (!von || !bis) return;
    if (stundenweise && (!vonZeit || !bisZeit)) {
      setError(t('abwesenheiten.zeitenPflicht', 'Bitte Von- und Bis-Zeit angeben'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (quelle === 'urlaub_antraege') {
        await apiPost('/api/urlaub/antraege', { mitarbeiter_id: parseInt(maId), von_datum: von, bis_datum: bis, typ, notiz_ma: notiz || undefined });
      } else if (quelle === 'krankenstaende') {
        await apiPost('/api/urlaub/krankenstaende', { mitarbeiter_id: parseInt(maId), von_datum: von, bis_datum: bis, typ, notiz: notiz || undefined });
      } else {
        await apiPost('/api/abwesenheiten', {
          mitarbeiter_id: parseInt(maId),
          art_key: typ,
          von_datum: von,
          bis_datum: bis,
          von_zeit: stundenweise ? vonZeit : undefined,
          bis_zeit: stundenweise ? bisZeit : undefined,
          grund: notiz || undefined,
        });
      }
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.fehler?.map(f => f.text || f).join(', ') || err.error || 'Error');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] w-full max-w-lg mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-card,#E5E7EB)]">
          <h2 className="text-base font-semibold text-[var(--text-primary)]">{t('abwesenheiten.neuerAntrag')}</h2>
          <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
              <AlertTriangle size={14} /> {error}
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('urlaub.antrag.typ', 'Typ')} *</label>
            <select className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)] text-[var(--text-primary)]"
              value={typ} onChange={e => setTyp(e.target.value)}>
              {abwesenheitsarten.filter(a => a.quelle === 'urlaub_antraege').length > 0 && (
                <optgroup label={t('urlaub.gruppe.urlaub', 'Urlaub')}>
                  {abwesenheitsarten.filter(a => a.quelle === 'urlaub_antraege').map(a => (
                    <option key={a.key} value={a.key}>{a.label_de}</option>
                  ))}
                </optgroup>
              )}
              {abwesenheitsarten.filter(a => a.quelle === 'krankenstaende').length > 0 && (
                <optgroup label={t('urlaub.gruppe.krankenstand', 'Krankenstand')}>
                  {abwesenheitsarten.filter(a => a.quelle === 'krankenstaende').map(a => (
                    <option key={a.key} value={a.key}>{a.label_de}</option>
                  ))}
                </optgroup>
              )}
              {abwesenheitsarten.filter(a => a.quelle === 'abwesenheiten').length > 0 && (
                <optgroup label={t('urlaub.gruppe.abwesenheit', 'Sonstige')}>
                  {abwesenheitsarten.filter(a => a.quelle === 'abwesenheiten').map(a => (
                    <option key={a.key} value={a.key}>{a.label_de}</option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('urlaub.antrag.von', 'Von')} *</label>
              <input type="date" className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)]"
                value={von} onChange={e => setVon(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('urlaub.antrag.bis', 'Bis')} *</label>
              <input type="date" className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)]"
                value={bis} onChange={e => setBis(e.target.value)} />
            </div>
          </div>
          {typ === 'urlaub' && verfuegbar != null && (
            <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-lg p-3 text-sm">
              <div className="flex justify-between">
                <span className="text-[var(--text-secondary)]">{t('urlaub.verfuegbareTage')}</span>
                <span className="font-bold text-[var(--text-primary)]">{Math.round(verfuegbar * 100) / 100} {t('urlaub.tage')}</span>
              </div>
            </div>
          )}
          {/* #1005 Stundenweise-Toggle: nur für Abwesenheits-Arten mit erlaubt_stundenweise=true */}
          {selectedArt?.erlaubt_stundenweise && quelle === 'abwesenheiten' && (
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-[var(--text-primary)] cursor-pointer">
                <input type="checkbox" className="rounded border-[var(--border-default)]"
                  checked={stundenweise} onChange={e => setStundenweise(e.target.checked)} />
                {t('abwesenheiten.stundenweiseErfassen', 'Stundenweise erfassen')}
              </label>
              {stundenweise && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('abwesenheiten.vonZeit', 'Von Zeit')}</label>
                    <input type="time" className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)]"
                      value={vonZeit} onChange={e => setVonZeit(e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('abwesenheiten.bisZeit', 'Bis Zeit')}</label>
                    <input type="time" className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)]"
                      value={bisZeit} onChange={e => setBisZeit(e.target.value)} />
                  </div>
                </div>
              )}
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('urlaub.notizMa', 'Notiz')}</label>
            <textarea rows={2} className="w-full px-3 py-2 border border-[var(--border-default)] rounded-lg text-sm bg-[var(--bg-card)] resize-none"
              value={notiz} onChange={e => setNotiz(e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-[var(--border-default,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)]">
          <button onClick={onClose}
            className="px-4 py-2 rounded-[10px] border border-[var(--border-default,#E5E7EB)] text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover,#FAFAFE)]">
            {t('common.cancel', 'Abbrechen')}
          </button>
          <button onClick={handleSubmit} disabled={saving || !von || !bis}
            className="px-4 py-2 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold disabled:opacity-50 transition-colors">
            {saving ? '...' : t('urlaub.antragEinreichen')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════
export default function UrlaubUebersichtPage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const isAdmin = ['administrator', 'admin', 'ops_manager'].includes(user?.rolle);

  const [tab, setTab] = useTabParam('salden');
  const [loading, setLoading] = useState(true);
  const [konten, setKonten] = useState([]);
  const [stats, setStats] = useState({ avg_resturlaub: '0', verfallsgefaehrdet_count: 0, heute_abwesend: 0, antraege_offen: 0, ma_count: 0 });
  const [sperren, setSperren] = useState([]);
  const [regeln, setRegeln] = useState([]);
  const [saldoMaId, setSaldoMaId] = useState(null);
  const [antragModal, setAntragModal] = useState({ open: false, maId: null });
  const [recalcLoading, setRecalcLoading] = useState(false);

  const jahr = new Date().getFullYear();

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api(`/api/urlaub/konto?jahr=${jahr}`),
      api('/api/urlaub/statistiken'),
      api('/api/urlaub/sperren'),
      api('/api/urlaub/regeln'),
      api(`/api/urlaub/krankenstaende/statistiken?jahr=${jahr}`).catch(() => ({ data: {} })),
    ]).then(([k, s, sp, r, ks]) => {
      setKonten(k.data || []);
      const ksData = ks.data || {};
      setStats({ ...(s.data || stats), ks_avg_tage_pro_ma: ksData.avg_tage_pro_ma, ks_ma_ueber_schwelle: (ksData.ma_ueber_schwelle || []).length, ks_schwelle: ksData.schwelle });
      setSperren(sp.data || []);
      setRegeln(r.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [jahr]);

  useEffect(() => { load(); }, [load]);

  // Excel export
  const handleRecalcAll = async () => {
    setRecalcLoading(true);
    try {
      await apiPost('/api/urlaub/salden/recalc-all');
      load();
    } finally { setRecalcLoading(false); }
  };

  const handleExport = async () => {
    try {
      const res = await api(`/api/urlaub/export?jahr=${jahr}`);
      const { utils, writeFile } = await import('xlsx');

      // Sheet 1: Salden (v2.348.0: Zwei-Saldo-Logik)
      const ws1 = utils.json_to_sheet(res.data.konten.map(k => ({
        'Name': k.ma_name,
        'PNR': k.rzl_personalnummer,
        'Kostenstelle': k.kostenstelle,
        'Anspruch': parseFloat(k.anspruch_tage),
        'Vorjahr-Rest': parseFloat(k.vorjahr_rest),
        'Vorvorjahr-Rest': parseFloat(k.vorvorjahr_rest),
        'Genommen': parseFloat(k.genommen ?? k.verbraucht_tage),
        'Laufend (Anzahl)': parseInt(k.laufend_anzahl || 0),
        'Geplant': parseFloat(k.geplant ?? k.geplant_tage),
        'Geplant (Anzahl)': parseInt(k.geplant_anzahl || 0),
        'Aktuell verfügbar': parseFloat(k.aktuell_verfuegbar ?? 0),
        'Projiziert JE': parseFloat(k.projiziert_jahresende ?? 0),
      })));

      // Sheet 2: Anträge
      const ws2 = utils.json_to_sheet(res.data.antraege.map(a => ({
        'Name': a.ma_name,
        'Typ': a.typ,
        'Von': a.von_datum,
        'Bis': a.bis_datum,
        'Tage': parseFloat(a.tage),
        'Status': a.status,
      })));

      // Sheet 3: Verfall
      const ws3 = utils.json_to_sheet(res.data.verfall.map(v => ({
        'Name': v.ma_name,
        'Resturlaub Vorvorjahr': parseFloat(v.resturlaub_vorvorjahr),
        'Verfällt am': v.verfaellt_am,
      })));

      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws1, 'Salden');
      utils.book_append_sheet(wb, ws2, 'Anträge');
      utils.book_append_sheet(wb, ws3, 'Verfall-Warnungen');

      writeFile(wb, `Urlaubsuebersicht_${jahr}.xlsx`);
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-60 text-[var(--text-muted)]">
        <div className="w-5 h-5 border-2 border-[var(--primary)] border-t-transparent rounded-full animate-spin mr-2" />
        {t('common.loading') || 'Laden...'}
      </div>
    );
  }

  const verfallCount = konten.filter(k => parseFloat(k.vorvorjahr_rest) > 0).length;

  return (
    <div className="p-6 max-w-full">
      <SharedAbwesenheitenTabs />
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="p-2 rounded-xl bg-purple-50">
          <Calendar size={20} className="text-[#8B5CF6]" />
        </div>
        <h1 className="text-2xl font-bold text-[var(--text-primary)]">{t('abwesenheiten.uebersichtTitel')}</h1>
      </div>

      {/* Tab bar */}
      <TabBar
        tabs={[
          { key: 'salden', label: t('urlaub.uebersicht.salden') },
          { key: 'verfall', label: t('urlaub.uebersicht.verfall'), count: verfallCount || undefined },
          { key: 'sperren', label: t('urlaub.uebersicht.sperren'), count: sperren.filter(s => s.aktiv).length || undefined },
          { key: 'regeln', label: t('urlaub.uebersicht.regeln') },
          ...(isAdmin ? [{ key: 'einstellungen', label: t('modulSettings.title', 'Einstellungen') }] : []),
        ]}
        active={tab}
        onChange={setTab}
      />

      {/* Tab content */}
      {tab === 'salden' && <SaldenTab t={t} konten={konten} stats={stats} onExport={handleExport} onRowClick={(maId) => setSaldoMaId(maId)} onRecalcAll={handleRecalcAll} recalcLoading={recalcLoading} />}
      {tab === 'verfall' && <VerfallTab t={t} konten={konten} isAdmin={isAdmin} />}
      {tab === 'sperren' && <SperrenTab t={t} sperren={sperren} onReload={load} isAdmin={isAdmin} />}
      {tab === 'regeln' && <RegelnTab t={t} regeln={regeln} onReload={load} isAdmin={isAdmin} />}
      {tab === 'einstellungen' && <ModulEinstellungen kategorien={['urlaub', 'krankenstand']} />}

      {/* Saldo drawer */}
      {saldoMaId && (
        <SaldoDrawer
          maId={saldoMaId}
          onClose={() => setSaldoMaId(null)}
          onAntragErstellen={(maId) => {
            setSaldoMaId(null);
            setAntragModal({ open: true, maId });
          }}
          t={t}
          isAdmin={isAdmin}
        />
      )}

      {/* Neuer Antrag modal (from SaldoDrawer) */}
      {antragModal.open && (
        <NeuAntragDrawerModal
          maId={antragModal.maId}
          onClose={() => setAntragModal({ open: false, maId: null })}
          onSaved={load}
          t={t}
        />
      )}
    </div>
  );
}
